import React, {Dispatch, FC, SetStateAction, useEffect} from 'react';
import {CustomModal} from "../../../../../../components/UI/AntCustom/CustomModal";
import {defineMessages, useIntl} from "react-intl";
import {Form, message, DatePicker, Row, Col} from "antd";
import {CommonFormMessages} from "../../../../../../intl/commonMessages/CommonFormMessages";
import {CustomFieldInput} from "../../../../../../components/UI/AntCustom/CustomFieldInput";
import {CommonButtonMessages} from "../../../../../../intl/commonMessages/CommonButtonMessages";
import {CustomButton} from "../../../../../../components/UI/AntCustom/CustomButton";
import {
  IFlyHourCalculatorCategory,
  IFlyHourCalculatorSettingType,
  IFlyHourCalculatorSubCategory
} from "../../../../../../store/reducers/fleetReducer/fleetReducerTypes";
import {useFlyPriceCategorySetter} from "./hooks/useFlyPriceCategorySetter";
import {onFinishTrimmed} from "../../../../../../utils/helpers/formHelpers/onFinishTrimmed";
import moment, {Moment} from "moment";
import {
  FlyHourPriceDefaultSettingTypeEnum
} from "./types/flyHourPriceSettingEnums";

const FlyHourPriceCalculatorAddPeriodModalMessages = defineMessages({
  title: {
    id: "flyHourPriceCalculatorAddPeriodModal.title",
    defaultMessage: "Add new period"
  },
  dateFrom: {
    id: "flyHourPriceCalculator.dateFrom",
    defaultMessage: "Date From"
  },
  dateTo: {
    id: "flyHourPriceCalculator.dateTo",
    defaultMessage: "Date To"
  }
})

interface IFlyHourPriceCalculatorAddPeriodModal {
  isModalVisible: boolean;
  setIsModalVisible: Dispatch<SetStateAction<boolean>>;
  category: IFlyHourCalculatorCategory;
  subCategory: IFlyHourCalculatorSubCategory;
  mainValueField: IFlyHourCalculatorSettingType;
}

export const FlyHourPriceCalculatorAddPeriodModal: FC<IFlyHourPriceCalculatorAddPeriodModal> = ({
  isModalVisible,
  setIsModalVisible,
  category,
  subCategory,
  mainValueField
}) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const categorySetter = useFlyPriceCategorySetter(category.keyName);

  useEffect(() => {
    if (isModalVisible) {
      form.resetFields();
    }
  }, [isModalVisible, form]);

  const onFinish = (values: { dateFrom?: Moment, dateTo?: Moment }) => {
    if (!categorySetter || !mainValueField) return;

    const baseKeyName = mainValueField.keyName;
    const newPeriod: IFlyHourCalculatorSettingType = {
      keyName: `${baseKeyName}_period_${Date.now()}`,
      label: mainValueField.label,
      isEnabled: true,
      isDefault: false,
      setting: {
        value: mainValueField.setting.value ?? 0, // Inherit value from main field
        specificType: mainValueField.setting.specificType || mainValueField.type,
        dateFrom: values.dateFrom ? values.dateFrom.format('YYYY-MM-DD') : undefined,
        dateTo: values.dateTo ? values.dateTo.format('YYYY-MM-DD') : undefined,
      },
      type: mainValueField.type,
    };

    categorySetter(c => {
      return {
        ...c, subcategories: c.subcategories.map(sc => {
          if (sc.keyName !== subCategory.keyName)
            return sc;
          return {
            ...sc, settingTypes: [...sc.settingTypes, newPeriod]
          }
        })
      }
    });

    form.resetFields();
    setIsModalVisible(false);
  }

  const onFinishFailed = () => {
    message.error(intl.formatMessage(CommonFormMessages.incorrect))
  }

  const handleCancel = () => {
    form.resetFields();
    setIsModalVisible(false);
  };

  return (
    <CustomModal
      title={intl.formatMessage(FlyHourPriceCalculatorAddPeriodModalMessages.title)}
      open={isModalVisible}
      onCancel={handleCancel}
      centered={true}
      footer={null}
    >
      <Form
        form={form}
        onFinish={(values) => onFinishTrimmed(values, onFinish)}
        onFinishFailed={onFinishFailed}
        layout={'vertical'}
      >
        <Row gutter={16}>
          <Col span={12}>
            <CustomFieldInput
              label={intl.formatMessage(FlyHourPriceCalculatorAddPeriodModalMessages.dateFrom)}
              name={'dateFrom'}
              rules={[
                {required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)},
              ]}
            >
              <DatePicker
                format="YYYY-MM-DD"
                placeholder="YYYY-MM-DD"
                style={{width: "100%"}}
                showToday={true}
              />
            </CustomFieldInput>
          </Col>
          <Col span={12}>
            <CustomFieldInput
              label={intl.formatMessage(FlyHourPriceCalculatorAddPeriodModalMessages.dateTo)}
              name={'dateTo'}
              rules={[
                {required: true, message: intl.formatMessage(CommonFormMessages.ruleRequired)},
              ]}
            >
              <DatePicker
                format="YYYY-MM-DD"
                placeholder="YYYY-MM-DD"
                style={{width: "100%"}}
                showToday={true}
              />
            </CustomFieldInput>
          </Col>
        </Row>

        <Row gutter={8} style={{marginTop: 16}}>
          <Col span={12}>
            <CustomButton
              type={'default'}
              onClick={handleCancel}
              style={{width: "100%"}}
            >
              {intl.formatMessage(CommonButtonMessages.cancel)}
            </CustomButton>
          </Col>
          <Col span={12}>
            <CustomButton
              type={'primary'}
              htmlType={'submit'}
              style={{width: "100%"}}
            >
              {intl.formatMessage(CommonButtonMessages.save)}
            </CustomButton>
          </Col>
        </Row>
      </Form>
    </CustomModal>
  );
};







